import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LegalEntityFormGroup } from '@app/models/form.models';
import { LegalEntity } from '@app/models/project.models';
import { GetLocalizedPipe } from '@app/pipes/get-localized.pipe';
import { LegalPersonService } from '@app/services/profile/legal-person.service';
import { GenericStore } from '@app/stores/generic.store';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-legal-person-validated-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ValidatedInputComponent,
    ValidatedDropdownComponent,
    GetLocalizedPipe,
    ButtonComponent,
  ],
  templateUrl: './legal-person-validated-form.component.html',
  styleUrl: './legal-person-validated-form.component.scss',
})
export class LegalPersonValidatedFormComponent implements OnInit {
  public formGroupInput = input.required<FormGroup<LegalEntityFormGroup>>();
  public taxNumberDisabled = input<boolean>(false);
  public showFillDataButton = input<boolean>(false);
  /** When false, the optional bank account block is hidden. Default true. */
  public showBankAccount = input<boolean>(true);
  public disabled = input<boolean>(false);

  protected store = inject(GenericStore);

  protected matchedLegalPerson = signal<LegalEntity | undefined>(undefined);

  protected companyTypes = computed(() => {
    const countryCode = this.selectedCountryCode();

    return countryCode
      ? this.store
          .companyTypesEntities()
          .filter((companyType) =>
            companyType.countriesUsed.includes(countryCode),
          )
      : [];
  });

  private selectedCountryCode = signal<undefined | string>(undefined);

  private legalPersonSerivce = inject(LegalPersonService);

  constructor() {
    effect(() => {
      const fg = this.formGroupInput();
      const ctrl = fg.controls.taxNumber;
      if (this.taxNumberDisabled()) {
        ctrl.disable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    const ownershipCtrl = this.formGroupInput().controls.ownershipStructure;
    if (
      (ownershipCtrl.value === null || ownershipCtrl.value === undefined) &&
      this.store.ownershipStructureEntities().length > 0
    ) {
      const first = this.store.ownershipStructureEntities()[0];
      ownershipCtrl.setValue(first.id, { emitEvent: false });
    }

    if (this.showFillDataButton()) {
      this.formGroupInput().controls.taxNumber.valueChanges.subscribe(
        (value) => {
          if (value.length > 4) {
            this.legalPersonSerivce
              .getLegalEntities(value)
              .subscribe((response) => {
                if (response.data.length > 0) {
                  const source = response.data.find((entity) =>
                    this.isTaxMatch(entity.taxNumber, value),
                  );
                  if (source) {
                    this.formGroupInput().patchValue({
                      name: source.name,
                    });
                    this.matchedLegalPerson.set(source);
                  } else {
                    this.matchedLegalPerson.set(undefined);
                  }
                } else {
                  this.matchedLegalPerson.set(undefined);
                  this.formGroupInput().patchValue({
                    name: undefined,
                  });
                }
              });
          }
        },
      );
    }

    this.formGroupInput().controls.country.valueChanges.subscribe((value) => {
      this.selectedCountryCode.set(value);
      this.formGroupInput().controls.address.controls.countryCode.setValue(
        value,
      );
    });
  }

  fillWithExistingData() {
    const source = this.matchedLegalPerson();
    if (source == undefined) return;
    this.formGroupInput().patchValue({
      name: source.name,
      taxNumber: source.taxNumber,
      email: source.email,
      country: source.country,
      address: {
        city: source.address.city ?? undefined,
        countryCode: source.address.countryCode ?? undefined,
        doorOrFloor: source.address.doorOrFloor ?? undefined,
        houseNumber: source.address.houseNumber ?? undefined,
        publicSpace: source.address.publicSpace ?? undefined,
        publicSpaceType: source.address.publicSpaceType ?? undefined,
        zipCode: source.address.zipCode ?? undefined,
      },
      bankAccountNumber: source.bankAccountNumber,
    });
  }

  private isTaxMatch(taxNumber: string, input: string): boolean {
    return taxNumber.includes(input);
  }
}
